// SPDX-License-Identifier: BUSL-1.1

/**
 * Sign-up page — Better Auth email/password registration.
 *
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  fetchAndStoreBearerJwt,
  getLastBearerJwtBootstrapFailure,
  useAuth,
} from '@/lib/auth';
import { AUTH_BASE } from '@/lib/api-client';
import { showError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const signUpSchema = z
  .object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignUpFormValues = z.infer<typeof signUpSchema>;

function describeBootstrapFailure(
  failure = getLastBearerJwtBootstrapFailure(),
): string {
  if (!failure) {
    return 'Account created, but we could not mint the API token required for the dashboard.';
  }
  if (failure.code === 'token_endpoint_error') {
    return failure.status === 401
      ? 'Account created, but the API rejected the session while minting the dashboard token.'
      : `Account created, but the dashboard token bootstrap failed with HTTP ${failure.status}.`;
  }
  if (failure.code === 'network_error') {
    return 'Account created, but the dashboard token bootstrap could not reach the API.';
  }
  return 'Account created, but the dashboard token bootstrap returned no token.';
}

export function SignUpPage() {
  const { loginWithSession } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  });

  async function onSubmit(values: SignUpFormValues) {
    setServerError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${AUTH_BASE}/api/auth/sign-up/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: values.email,
          password: values.password,
          name: values.name,
        }),
      });

      if (res.ok) {
        // After sign-up, Better Auth may have created a session automatically.
        // Try to fetch it to update React auth state.
        const sessionRes = await fetch(`${AUTH_BASE}/api/auth/get-session`, {
          credentials: 'include',
        });
        if (sessionRes.ok) {
          // Better Auth returns `null` (HTTP 200) when there is no session.
          const { user } = (await sessionRes.json()) ?? {};
          if (user) {
            // Mint a Bearer JWT from build-ceo-auth so apiFetch has a token to send
            // (build-ceo-auth cookie can't be used cross-site against the build.ceo API).
            const jwt = await fetchAndStoreBearerJwt();
            if (!jwt) {
              const message = describeBootstrapFailure();
              setServerError(message);
              showError('Dashboard bootstrap failed', message);
              return;
            }
            loginWithSession(user);
          }
        }
        navigate('/');
      } else {
        const data = await res.json().catch(() => ({}));
        setServerError(data.error ?? 'Sign-up failed. Please try again.');
        showError('Sign-up failed', data.error ?? 'Please try again.');
      }
    } catch {
      setServerError('Could not reach the server. Check your connection.');
      showError('Network error', 'Could not reach the server.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950">
      <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-8">
        {/* Logo */}
        <div className="mb-8 font-mono text-xl font-bold tracking-tight text-neutral-100">
          build.ceo<span className="animate-pulse text-emerald-400">_</span>
        </div>

        <h1 className="mb-6 text-lg font-semibold text-neutral-200">Create account</h1>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-400">Name</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="name"
                      placeholder="Ada Lovelace"
                      className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-400">Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="ada@example.com"
                      className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-400">Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Min 8 characters"
                      className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-400">Confirm Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError && (
              <p className="rounded border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-400">
                {serverError}
              </p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 bg-emerald-600 text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
        </Form>

        <p className="mt-4 text-center text-sm text-neutral-500">
          Already have an account?{' '}
          <Link to="/sign-in" className="text-emerald-400 hover:text-emerald-300">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
